import { ShieldExclamationIcon } from '@heroicons/react/24/outline';

import type { TabOption } from '@/components/chariot-ui';
import { CopyToClipboard } from '@/components/CopyToClipboard';
import type {
  MainTabKey,
  POEContent,
  SectionContent,
} from '@/sections/poe/types/poe.types';
import { formatKey } from '@/sections/poe/utils/formatKey';

const getOrcaSeverityClass = (severity: string | null): string => {
  if (!severity) return '';
  const lower = severity.toLowerCase();
  if (lower.includes('critical')) return 'text-error font-medium';
  if (lower.includes('high')) return 'text-error font-medium';
  if (lower.includes('medium')) return 'text-warning font-medium';
  if (lower.includes('low')) return 'text-success font-medium';
  return 'text-chariot-text-tertiary';
};

const getOrcaScoreColor = (score: number): string => {
  if (score >= 9.0) return 'text-error';
  if (score >= 7.0) return 'text-warning';
  if (score >= 4.0) return 'text-warning';
  return 'text-success';
};

export const processOrcaDataSource = (
  poeData: POEContent,
  sections: Record<string, SectionContent>,
  tempTabs: Record<MainTabKey, TabOption[]>,
  mainTabKeys: MainTabKey[]
): void => {
  const processedKeys: string[] = [];

  try {
    const contentStr =
      typeof poeData === 'string' ? poeData : JSON.stringify(poeData);

    let orcaData: Record<string, string> | null = null;

    // Try JSON first, then key-value text format
    try {
      orcaData = JSON.parse(contentStr);
    } catch {
      // Parse key-value format: "key: value\n"
      const lines = contentStr.split('\n');
      const parsed: Record<string, string> = {};
      let hasData = false;

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        const colonIndex = trimmed.indexOf(':');
        if (colonIndex > 0) {
          const key = trimmed.substring(0, colonIndex).trim();
          const value = trimmed.substring(colonIndex + 1).trim();
          if (key && value) {
            parsed[key] = value;
            hasData = true;
          }
        }
      }

      if (hasData) {
        orcaData = parsed;
      }
    }

    if (!orcaData) return;

    const alertId = orcaData.orca_alert_id || '';
    const category = orcaData.category || '';
    const alertType = orcaData.type || '';
    const orcaScore = parseFloat(orcaData.orca_score || '0');
    const riskLevel = orcaData.risk_level || '';
    const cloudProvider = orcaData.cloud_provider || '';
    const cloudAccount = orcaData.cloud_account || '';
    const assetType = orcaData.asset_type || '';
    const assetName = orcaData.asset_name || '';
    const details = orcaData.details || '';
    const ruleQuery = orcaData.rule_query || '';
    const alertLabels = orcaData.alert_labels || '';

    // Parse MITRE labels from alert_labels (comma-separated)
    const labels = alertLabels
      ? alertLabels
          .split(',')
          .map(l => l.trim())
          .filter(Boolean)
      : [];
    const mitreLabels = labels.filter(l =>
      l.toLowerCase().startsWith('mitre:')
    );
    const otherLabels = labels.filter(
      l => !l.toLowerCase().startsWith('mitre:')
    );

    // --- INFORMATION tab: Alert Overview ---
    if (mainTabKeys.includes('INFORMATION')) {
      const severityClass = getOrcaSeverityClass(riskLevel);
      const isCriticalOrHigh =
        riskLevel.toLowerCase().includes('critical') ||
        riskLevel.toLowerCase().includes('high');
      const isMedium = riskLevel.toLowerCase().includes('medium');
      const borderColor = isCriticalOrHigh
        ? 'border-error'
        : isMedium
          ? 'border-warning'
          : 'border-success';

      sections['orca_overview'] = (
        <div className="space-y-4">
          {/* Severity banner with category */}
          <div className={`${borderColor} border-l-4 p-4`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <ShieldExclamationIcon
                  className={`mr-2 size-5 ${severityClass}`}
                />
                <h4 className="text-chariot-text-primary font-semibold">
                  {category || 'Orca Security Alert'}
                </h4>
              </div>
              {riskLevel && (
                <span
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${
                    isCriticalOrHigh
                      ? 'bg-error/10 text-error'
                      : isMedium
                        ? 'bg-warning/10 text-warning'
                        : 'bg-success/10 text-success'
                  }`}
                >
                  {riskLevel.toUpperCase()}
                </span>
              )}
            </div>
          </div>

          {/* Score + Provider hero row */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {/* Orca Score card */}
            {orcaScore > 0 && (
              <div className="border-chariot-stroke rounded-lg border p-4 text-center">
                <div className="text-chariot-text-secondary mb-1 text-xs font-medium tracking-wider uppercase">
                  Orca Score
                </div>
                <div
                  className={`text-3xl font-bold ${getOrcaScoreColor(orcaScore)}`}
                >
                  {orcaScore.toFixed(1)}
                </div>
                <div
                  className={`mt-1 text-xs ${getOrcaSeverityClass(riskLevel)}`}
                >
                  {riskLevel}
                </div>
              </div>
            )}

            {/* Cloud Provider card */}
            {cloudProvider && (
              <div className="border-chariot-stroke rounded-lg border p-4 text-center">
                <div className="text-chariot-text-secondary mb-1 text-xs font-medium tracking-wider uppercase">
                  Cloud Provider
                </div>
                <div className="text-chariot-text-primary text-lg font-bold uppercase">
                  {cloudProvider}
                </div>
                {cloudAccount && (
                  <CopyToClipboard alwaysShowCopyIcon textToCopy={cloudAccount}>
                    <div className="text-chariot-text-tertiary mt-1 font-mono text-xs">
                      {cloudAccount}
                    </div>
                  </CopyToClipboard>
                )}
              </div>
            )}

            {/* Asset Type card */}
            {assetType && (
              <div className="border-chariot-stroke rounded-lg border p-4 text-center">
                <div className="text-chariot-text-secondary mb-1 text-xs font-medium tracking-wider uppercase">
                  Asset Type
                </div>
                <div className="text-chariot-text-primary text-lg font-bold">
                  {assetType}
                </div>
                {assetName && (
                  <CopyToClipboard alwaysShowCopyIcon textToCopy={assetName}>
                    <div className="text-chariot-text-tertiary mt-1 text-xs">
                      {assetName}
                    </div>
                  </CopyToClipboard>
                )}
              </div>
            )}
          </div>

          {/* Alert details text */}
          {details && (
            <div className="border-chariot-stroke rounded-lg border p-4">
              <h5 className="text-chariot-text-primary mb-2 font-medium">
                Details
              </h5>
              <div className="text-chariot-text-primary text-sm leading-relaxed whitespace-pre-wrap">
                {details}
              </div>
            </div>
          )}

          {/* MITRE ATT&CK labels */}
          {mitreLabels.length > 0 && (
            <div className="border-chariot-stroke rounded-lg border p-4">
              <h5 className="text-chariot-text-primary mb-3 font-medium">
                MITRE ATT&CK Mapping
              </h5>
              <div className="flex flex-wrap gap-2">
                {mitreLabels.map((label, idx) => {
                  const technique = label.replace(/^mitre:\s*/i, '').trim();
                  return (
                    <span
                      key={idx}
                      className="border-chariot-stroke bg-chariot-background-secondary text-chariot-text-primary inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium"
                    >
                      <ShieldExclamationIcon className="text-chariot-text-secondary mr-1.5 size-3" />
                      {technique}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Other labels */}
          {otherLabels.length > 0 && (
            <div className="border-chariot-stroke rounded-lg border p-4">
              <h5 className="text-chariot-text-primary mb-3 font-medium">
                Alert Labels
              </h5>
              <div className="flex flex-wrap gap-2">
                {otherLabels.map((label, idx) => (
                  <span
                    key={idx}
                    className="border-chariot-stroke bg-chariot-background-secondary text-chariot-text-primary inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium"
                  >
                    {label}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      );

      tempTabs['INFORMATION'].push({
        label: 'Alert Overview',
        value: 'orca_overview',
      });
      processedKeys.push(
        'orca_alert_id',
        'category',
        'type',
        'orca_score',
        'risk_level',
        'cloud_provider',
        'cloud_account',
        'details',
        'alert_labels',
        'asset_name'
      );
    }

    // --- DETECTION tab: Alert Details ---
    if (mainTabKeys.includes('DETECTION')) {
      sections['orca_alert_details'] = (
        <div className="space-y-4">
          {/* Alert identification */}
          <div className="border-chariot-stroke overflow-hidden rounded-lg border">
            <table className="w-full">
              <tbody>
                {alertId && (
                  <tr className="border-chariot-stroke border-b last:border-b-0">
                    <td className="bg-chariot-background-secondary text-chariot-text-secondary w-1/3 px-4 py-3 font-medium">
                      Alert ID
                    </td>
                    <td className="text-chariot-text-primary px-4 py-3">
                      <CopyToClipboard alwaysShowCopyIcon textToCopy={alertId}>
                        <code className="bg-chariot-background-secondary rounded px-2 py-1 text-sm">
                          {alertId}
                        </code>
                      </CopyToClipboard>
                    </td>
                  </tr>
                )}
                {category && (
                  <tr className="border-chariot-stroke border-b last:border-b-0">
                    <td className="bg-chariot-background-secondary text-chariot-text-secondary w-1/3 px-4 py-3 font-medium">
                      Category
                    </td>
                    <td className="text-chariot-text-primary px-4 py-3">
                      {category}
                    </td>
                  </tr>
                )}
                {alertType && (
                  <tr className="border-chariot-stroke border-b last:border-b-0">
                    <td className="bg-chariot-background-secondary text-chariot-text-secondary w-1/3 px-4 py-3 font-medium">
                      Alert Type
                    </td>
                    <td className="text-chariot-text-primary px-4 py-3">
                      <CopyToClipboard
                        alwaysShowCopyIcon
                        textToCopy={alertType}
                      >
                        <code className="text-chariot-text-primary font-mono text-sm">
                          {alertType}
                        </code>
                      </CopyToClipboard>
                    </td>
                  </tr>
                )}
                {orcaScore > 0 && (
                  <tr className="border-chariot-stroke border-b last:border-b-0">
                    <td className="bg-chariot-background-secondary text-chariot-text-secondary w-1/3 px-4 py-3 font-medium">
                      Orca Score
                    </td>
                    <td className="text-chariot-text-primary px-4 py-3">
                      <span
                        className={`text-lg font-bold ${getOrcaScoreColor(orcaScore)}`}
                      >
                        {orcaScore.toFixed(1)}
                      </span>
                      <span
                        className={`ml-2 text-xs ${getOrcaSeverityClass(riskLevel)}`}
                      >
                        ({riskLevel})
                      </span>
                    </td>
                  </tr>
                )}
                {!orcaScore && riskLevel && (
                  <tr className="border-chariot-stroke border-b last:border-b-0">
                    <td className="bg-chariot-background-secondary text-chariot-text-secondary w-1/3 px-4 py-3 font-medium">
                      Risk Level
                    </td>
                    <td className="text-chariot-text-primary px-4 py-3">
                      <span className={getOrcaSeverityClass(riskLevel)}>
                        {riskLevel}
                      </span>
                    </td>
                  </tr>
                )}
                {cloudProvider && (
                  <tr className="border-chariot-stroke border-b last:border-b-0">
                    <td className="bg-chariot-background-secondary text-chariot-text-secondary w-1/3 px-4 py-3 font-medium">
                      Cloud Provider
                    </td>
                    <td className="text-chariot-text-primary px-4 py-3 uppercase">
                      {cloudProvider}
                    </td>
                  </tr>
                )}
                {cloudAccount && (
                  <tr className="border-chariot-stroke border-b last:border-b-0">
                    <td className="bg-chariot-background-secondary text-chariot-text-secondary w-1/3 px-4 py-3 font-medium">
                      Cloud Account
                    </td>
                    <td className="text-chariot-text-primary px-4 py-3">
                      <CopyToClipboard
                        alwaysShowCopyIcon
                        textToCopy={cloudAccount}
                      >
                        <span className="font-mono">{cloudAccount}</span>
                      </CopyToClipboard>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Rule Query section (like Wiz configuration rule details) */}
          {ruleQuery && (
            <div className="border-chariot-stroke rounded-lg border p-4">
              <h5 className="text-chariot-text-primary mb-2 font-medium">
                Detection Rule
              </h5>
              <div className="bg-chariot-background-secondary rounded-lg p-3">
                <CopyToClipboard alwaysShowCopyIcon textToCopy={ruleQuery}>
                  <code className="text-chariot-text-primary text-sm break-all">
                    {ruleQuery}
                  </code>
                </CopyToClipboard>
              </div>
            </div>
          )}
        </div>
      );

      tempTabs['DETECTION'].push({
        label: 'Alert Details',
        value: 'orca_alert_details',
      });
      processedKeys.push('rule_query');
    }

    // --- ATTRIBUTES tab: Asset Information ---
    if (mainTabKeys.includes('ATTRIBUTES') && (assetType || assetName)) {
      const knownKeys = new Set([
        'orca_alert_id',
        'category',
        'type',
        'orca_score',
        'risk_level',
        'cloud_provider',
        'cloud_account',
        'asset_type',
        'asset_name',
        'details',
        'rule_query',
        'alert_labels',
      ]);

      // Collect any additional properties not in the known set
      const additionalProps = Object.entries(orcaData)
        .filter(([key]) => !knownKeys.has(key))
        .filter(([, value]) => value && String(value).trim() !== '');

      sections['orca_asset_info'] = (
        <div className="space-y-4">
          {/* Asset details table (matching Wiz endpoint/subscription style) */}
          <div className="border-chariot-stroke overflow-hidden rounded-lg border">
            <div className="border-chariot-stroke border-b px-4 py-3">
              <div className="flex items-center justify-between">
                <h4 className="text-chariot-text-primary font-semibold">
                  Cloud Resource
                </h4>
                {alertId && (
                  <span className="border-chariot-stroke bg-chariot-background-secondary text-chariot-text-primary inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium">
                    Orca Alert: {alertId}
                  </span>
                )}
              </div>
            </div>
            <table className="w-full">
              <tbody>
                {assetName && (
                  <tr className="border-chariot-stroke border-b last:border-b-0">
                    <td className="bg-chariot-background-secondary text-chariot-text-secondary w-1/3 px-4 py-3 font-medium">
                      Asset Name
                    </td>
                    <td className="text-chariot-text-primary px-4 py-3">
                      <CopyToClipboard
                        alwaysShowCopyIcon
                        textToCopy={assetName}
                      >
                        <span className="font-medium">{assetName}</span>
                      </CopyToClipboard>
                    </td>
                  </tr>
                )}
                {assetType && (
                  <tr className="border-chariot-stroke border-b last:border-b-0">
                    <td className="bg-chariot-background-secondary text-chariot-text-secondary w-1/3 px-4 py-3 font-medium">
                      Asset Type
                    </td>
                    <td className="text-chariot-text-primary px-4 py-3">
                      {assetType}
                    </td>
                  </tr>
                )}
                {cloudProvider && (
                  <tr className="border-chariot-stroke border-b last:border-b-0">
                    <td className="bg-chariot-background-secondary text-chariot-text-secondary w-1/3 px-4 py-3 font-medium">
                      Cloud Provider
                    </td>
                    <td className="text-chariot-text-primary px-4 py-3 uppercase">
                      {cloudProvider}
                    </td>
                  </tr>
                )}
                {cloudAccount && (
                  <tr className="border-chariot-stroke border-b last:border-b-0">
                    <td className="bg-chariot-background-secondary text-chariot-text-secondary w-1/3 px-4 py-3 font-medium">
                      Cloud Account ID
                    </td>
                    <td className="text-chariot-text-primary px-4 py-3">
                      <CopyToClipboard
                        alwaysShowCopyIcon
                        textToCopy={cloudAccount}
                      >
                        <span className="font-mono">{cloudAccount}</span>
                      </CopyToClipboard>
                    </td>
                  </tr>
                )}
                {additionalProps.map(([key, value]) => (
                  <tr
                    key={key}
                    className="border-chariot-stroke border-b last:border-b-0"
                  >
                    <td className="bg-chariot-background-secondary text-chariot-text-secondary w-1/3 px-4 py-3 font-medium">
                      {formatKey(key)}
                    </td>
                    <td className="text-chariot-text-primary px-4 py-3">
                      <CopyToClipboard
                        alwaysShowCopyIcon
                        textToCopy={String(value)}
                      >
                        <span>{String(value)}</span>
                      </CopyToClipboard>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );

      tempTabs['ATTRIBUTES'].push({
        label: 'Asset Information',
        value: 'orca_asset_info',
      });
      processedKeys.push('asset_type');
    }
  } catch (error) {
    console.error('Error processing Orca data:', error);
    sections['orca_error'] = (
      <div className="text-error text-sm">
        Failed to parse Orca proof data. Please check the raw evidence file.
      </div>
    );
    tempTabs['INFORMATION'].push({
      label: 'Error',
      value: 'orca_error',
    });
  }
};
